import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
    AbstractControl,
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder, UntypedFormControl,
    UntypedFormGroup, ValidationErrors,
    Validators
} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {DataService} from "../../services/data.service";
import {AccessService} from "../../services/access.service";
import {ViewMode} from "../../interfaces";
import Swal from "sweetalert2";
import {ToastrService} from "ngx-toastr";
import {Subscription} from "rxjs";
import {NgIf} from "@angular/common";
import {YesNoPipe} from "../../pipes/yes-no.pipe";

@Component({
    selector: 'app-user-detail',
    imports: [NgIf, ReactiveFormsModule, YesNoPipe, FormsModule],
    styles: `
        .table-rights tbody tr:last-child td {
            border-bottom: 0;
        }

        .table-rights th:first-child,
        .table-rights td:first-child {
            padding-left: 0
        }

        h5 {
            background-color: var(--primary-sidebar);
            padding: 0.25rem 0.5rem;
            border-radius: 2px;
        }
    `,
    template: `
        <div class="card">
            <div class="card-body">
                <div class="mb-4">
                    <h3>
                        <i class="fa-solid fa-users me-2"></i>
                        @switch (viewMode) {
                            @case (ViewMode.INSERT) {
                                Benutzer anlegen
                            }
                            @case (ViewMode.EDIT) {
                                Benutzer bearbeiten
                            }
                            @case (ViewMode.VIEW) {
                                Benutzer
                            }
                        }
                    </h3>
                </div>

                <form [formGroup]="form">
                    <h5 class="mt-3 mb-2">Name</h5>
                    <div class="row">
                        <div class="col">
                            <label>Vorname</label>
                            <input type="text" class="form-control" formControlName="firstname" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW">{{ form.value.firstname }}</p>
                        </div>
                        <div class="col">
                            <label>Nachname</label>
                            <input type="text" class="form-control" formControlName="lastname" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW">{{ form.value.lastname }}</p>
                        </div>
                    </div>
                    <h5 class="mt-3 mb-2">Kontaktdaten</h5>
                    <div class="row">
                        <div class="col">
                            <label>E-Mail</label>
                            <input type="text" class="form-control" formControlName="email" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW">
                                <a [href]="'mailto:' + form.value.email">{{ form.value.email }}</a>
                            </p>
                        </div>
                        <div class="col">
                            <label>Mobil</label>
                            <input type="text" class="form-control" formControlName="phone" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW">
                                {{ form.value.phone }}
                                <span class="text-muted" *ngIf="!form.value.phone">Nicht gesetzt</span>
                            </p>
                        </div>
                    </div>
                    <h5 class="mt-3 mb-2">Berichteinstellungen</h5>
                    <div class="row">
                        <div class="col">
                            <label>Täglicher Bericht</label>
                            <div *ngIf="viewMode != ViewMode.VIEW">
                                <input type="checkbox" class="form-check-input" formControlName="daily_report">
                            </div>
                            <p class="gray" *ngIf="viewMode == ViewMode.VIEW">{{ form.value.daily_report | yesNo }}</p>
                        </div>
                        <div class="col">
                            <label>Monatlicher Bericht</label>
                            <div *ngIf="viewMode != ViewMode.VIEW">
                                <input type="checkbox" class="form-check-input" formControlName="monthly_report">
                            </div>
                            <p class="gray" *ngIf="viewMode == ViewMode.VIEW">{{ form.value.monthly_report | yesNo }}</p>
                        </div>
                    </div>

                    <h5 class="mt-3 mb-2">Status</h5>
                    <label class="form-label">Aktiv</label>
                    <div *ngIf="viewMode != ViewMode.VIEW">
                        <input type="checkbox" class="form-check-input" formControlName="active">
                    </div>
                    <p class="gray" *ngIf="viewMode == ViewMode.VIEW">{{ form.value.active | yesNo }}</p>

                    <h5 class="mt-3 mb-2" *ngIf="viewMode != ViewMode.VIEW">Passwort</h5>
                    <div class="row" *ngIf="viewMode != ViewMode.VIEW">
                        <div class="col">
                            <label>Passwort</label>
                            <input type="password" class="form-control" formControlName="password">
                        </div>
                        <div class="col">
                            <label>Passwort wiederholen</label>
                            <input type="password" class="form-control" formControlName="confirmPassword">
                        </div>
                    </div>
                    <small class="text-muted" *ngIf="viewMode == ViewMode.EDIT">Das Passwort muss nur ausgefüllt werden, wenn es geändert werden soll</small>
                </form>

                <h5 class="mt-3 mb-2">Rechte</h5>
                <table class="table table-sm table-rights">
                    <thead>
                    <tr>
                        <th>Name</th>
                        <th>Erlaubt</th>
                    </tr>
                    </thead>
                    <tbody>
                        @for (right of rights; track right.id) {
                            <tr>
                                <td>{{right.name}}</td>
                                <td style="width: 100px">
                                    <input type="checkbox" *ngIf="viewMode != ViewMode.VIEW" [(ngModel)]="right.granted">
                                    <div class="gray" *ngIf="viewMode == ViewMode.VIEW">
                                        <i class="fa-solid fa-check text-success" *ngIf="right.granted"></i>
                                        <i class="fa-solid fa-times text-danger" *ngIf="!right.granted"></i>
                                        {{ right.granted | yesNo }}
                                    </div>
                                </td>
                            </tr>
                        }
                    </tbody>
                </table>
                @if (viewMode != ViewMode.VIEW) {
                    <button type="button" class="btn btn-secondary btn-sm float-end" (click)="toggleRights(true)">Alle</button>
                    <button type="button" class="btn btn-secondary btn-sm float-end me-2" (click)="toggleRights(false)">Keine</button>
                    <div class="clearfix"></div>
                }

                <div class="mt-3">
                    @if (viewMode == ViewMode.VIEW) {
                        <button type="button" class="btn btn-primary me-2" (click)="setMode(ViewMode.EDIT)">Bearbeiten</button>
                        <button type="button" class="btn btn-secondary" (click)="cancel()">Zurück</button>
                        <!--<button type="button" class="btn btn-danger float-end" (click)="delete()">Löschen</button>-->
                    } @else if (viewMode == ViewMode.EDIT) {
                        <button type="button" class="btn btn-primary" [disabled]="form.invalid" (click)="update()">Aktualisieren</button>
                        <button type="button" class="btn btn-secondary ms-2" (click)="cancelEdit()">Abbrechen</button>
                    } @else if (viewMode == ViewMode.INSERT) {
                        <button type="button" class="btn btn-primary" [disabled]="form.invalid" (click)="save()">Speichern</button>
                        <button type="button" class="btn btn-secondary ms-2" (click)="cancel()">Abbrechen</button>
                    }
                </div>
            </div>
        </div>
    `
})
export class UserDetailComponent implements OnInit, OnDestroy {

    accessService = inject(AccessService);
    private router = inject(Router);
    private fb = inject(UntypedFormBuilder);
    private dataService = inject(DataService);
    private route = inject(ActivatedRoute);
    private toastrService = inject(ToastrService);

    readonly ViewMode = ViewMode;

    form!: UntypedFormGroup;
    id_user!: string;
    viewMode!: ViewMode;
    user: any;
    departments!: any[];
    rights!: any[];
    passwordChanges$!: Subscription;

    ngOnInit(): void {
        this.id_user = this.route.snapshot.paramMap.get('id') ?? '0';
        this.viewMode = this.id_user == '0' ? ViewMode.INSERT : ViewMode.VIEW;

        let password;
        let confirmPassword;
        if (this.id_user === '0') {
            password = new UntypedFormControl('', Validators.compose([Validators.required, Validators.minLength(6)]));
            confirmPassword = new UntypedFormControl('', [
                Validators.required,
                (confirmControl: UntypedFormControl): ValidationErrors | null => {
                    if (!confirmControl.parent) {
                        return null;
                    }

                    const passwordControl = confirmControl.parent.get('password');
                    if (!passwordControl || !confirmControl) return null;
                    return passwordControl.value === confirmControl.value ? null : {nomatch: true};
                }]
            );
        } else {
            password = new UntypedFormControl('', Validators.compose([Validators.minLength(6)]));
            confirmPassword = new UntypedFormControl('', [
                (confirmControl: AbstractControl): ValidationErrors | null => {
                    if (!confirmControl.parent) {
                        return null;
                    }

                    const passwordControl = confirmControl.parent.get('password');
                    if (!passwordControl || !confirmControl) return null;
                    return passwordControl.value === confirmControl.value ? null : {nomatch: true};
                }]
            );
        }

        this.form = this.fb.group({
            id: [null],
            firstname: [null, Validators.required],
            lastname: [null, Validators.required],
            phone: [null],
            email: [null, [Validators.required, Validators.email]],
            password,
            confirmPassword,
            active: [true],
            daily_report: [false],
            monthly_report: [false],
        });

        this.passwordChanges$ = this.form.get('password').valueChanges.subscribe(val => {
            this.form.get('confirmPassword').updateValueAndValidity();
        });

        if (this.id_user !== '0') {
            this.getUser();
        } else {
            this.dataService.request('Right/listAll').subscribe(response => {
                this.rights = response.rights;

                this.rights.forEach(right => {
                    right.granted = false;
                })
            });
        }

        this.dataService.request('Department/listForUser').subscribe(response => {
            this.departments = response.departments;
        });
    }

    ngOnDestroy() {
        this.passwordChanges$.unsubscribe();
    }

    getUser(): void {
        this.dataService.request('User/get', {
            id_user: this.id_user
        }).subscribe(response => {
            this.user = response.user;

            this.dataService.request('Right/listAll').subscribe(response => {
                this.rights = response.rights;

                this.rights.forEach(right => {
                    right.granted = this.user.rights.includes(right.code);
                })
            });

            this.patchForm();
        });
    }

    patchForm(): void {
        this.form.patchValue({
            id: this.user.id,
            firstname: this.user.firstname,
            lastname: this.user.lastname,
            phone: this.user.phone,
            email: this.user.email,
            active: this.user.active == 1,
            daily_report: this.user.daily_report == 1,
            monthly_report: this.user.monthly_report == 1,
        });
    }

    save(): void {
        this.dataService.request('User/save', {
            user: this.form.getRawValue(),
            new_rights: this.rights.filter(right => right.granted).map(right => right.id),
        }).subscribe(() => {
            this.router.navigate(['/user']);
            this.toastrService.success('Der Benutzer wurde angelegt');
        });
    }

    update(): void {
        this.dataService.request('User/update', {
            user: this.form.getRawValue(),
            new_rights: this.rights.filter(right => right.granted).map(right => right.id),
        }).subscribe(() => {
            this.setMode(ViewMode.VIEW);
            this.getUser();
            this.toastrService.success('Der Benutzer wurde aktualisiert');
        });
    }

    cancel(): void {
        this.router.navigate(['/user']);
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
    }

    delete(): void {
        Swal.fire({
            title: 'Sicher?',
            text: 'Der Benutzer wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('User/delete', {
                    id_user: this.id_user
                }).subscribe(() => {
                    this.cancel();
                    this.toastrService.success('Der Benutzer wurde gelöscht');
                });
            }
        });
    }

    toggleRights(granted: boolean) {
        this.rights.forEach(right => right.granted = granted);
    }

}
